import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react'
import { grayRamp, grayRampDark, blueRamp, blueRampDark, brandRamp, radiiScale, containerSizes, chrome, status, fontFamilies, shadows as shadowScale,
  vizCat, vizCatDark, vizSeq, vizSeqDark, vizDiv, vizDivDark, vizParty, vizPartyDark } from './tokens'
import { conditions } from './conditions'
import { recipes, slotRecipes } from './recipes'

const ramp = (r: Record<string | number, string>) =>
  Object.fromEntries(Object.entries(r).map(([k, v]) => [k, { value: v }]))

const radii = Object.fromEntries(
  Object.entries(radiiScale).map(([k, v]) => [k, { value: v }]),
)

const containerSizeTokens = Object.fromEntries(
  Object.entries(containerSizes).map(([k, v]) => [k, { value: v }]),
)

// surface + paired fg; ink is the shared foreground
const surface = (c: { light: string; dark: string }) => ({
  value: { _light: c.light, _dark: c.dark },
})
const inkFg = { value: { _light: chrome.ink.light, _dark: chrome.ink.dark } }

const dualRamp = (light: Record<string, string>, dark: Record<string, string>) =>
  Object.fromEntries(
    Object.keys(light).map((k) => [k, { value: { _light: light[k], _dark: dark[k] } }]),
  )

// Single, mode-agnostic token reference. The referenced ramp step is itself
// mode-aware (dualRamp), so one reference supplies BOTH modes' values — see the
// reconciliation block below.
const ref = (token: string) => ({ value: `{colors.${token}}` })

const config = defineConfig({
  conditions,
  globalCss: {
    body: {
      bg: 'canvas',
      color: 'ink',
    },
  },
  theme: {
    tokens: {
      fonts: {
        heading: { value: fontFamilies.sans },
        body: { value: fontFamilies.sans },
        mono: { value: fontFamilies.mono },
      },
      colors: {
        gray: ramp(grayRamp),
        brand: ramp(brandRamp),
      },
      radii,
      sizes: { container: containerSizeTokens },
    },
    semanticTokens: {
      // shadows made mode-aware — light values byte-identical to slice-4, dark
      // variants deepen to near-black. Conditions are rejected in the base tokens
      // layer (same rule as colours), so shadows live here like gray/blue. Consumed
      // only as shadow="popover"/"modal", which resolve fine from a semantic token.
      shadows: {
        popover: { value: { _light: shadowScale.popover.light, _dark: shadowScale.popover.dark } },
        modal:   { value: { _light: shadowScale.modal.light,   _dark: shadowScale.modal.dark } },
      },
      colors: {
        // gray ramp made mode-aware — light values identical to the base ramp,
        // dark values from grayRampDark. All 1143 raw gray.NN sites flip per-mode
        // with zero call-site edits. (base tokens.colors.gray stays for colorPalette.)
        //
        // RECONCILIATION (slice 7.1): our gray ramp is mode-aware AND reflected
        // (gray.50 = light surface / dark canvas, gray.900 = light ink / dark ink).
        // Chakra's stock gray *virtual* tokens switch STEPS per mode assuming a fixed
        // ramp (fg = gray.800 light / gray.200 dark, subtle = gray.100 / gray.900, …),
        // which DOUBLE-INVERTS against our reflection: in dark, `_dark:{gray.200}`
        // resolves to our LIGHT surface (#1E231F is dark, #EDEEEA is light ink), giving
        // invisible button text, near-white "subtle" fills, and bright borders. Re-point
        // each virtual at a SINGLE step (== its stock LIGHT step, so light stays
        // byte-identical) and let the mode-aware var flip once. gray.solid drops the
        // literal-white dark and follows the ramp too.
        gray: {
          ...dualRamp(grayRamp, grayRampDark),
          fg: ref('gray.800'),
          subtle: ref('gray.100'),
          muted: ref('gray.200'),
          emphasized: ref('gray.300'),
          solid: ref('gray.900'),
          border: ref('gray.200'),
        },
        // blue made mode-aware for dark-safety. Not part of the Field Green identity —
        // the chrome-blue -> brand/info migration is slice 8. Frozen by the blue.NN ratchet.
        // Its virtuals get the same single-step reconciliation as gray (blue is reflected too).
        blue: {
          ...dualRamp(blueRamp, blueRampDark),
          fg: ref('blue.700'),
          subtle: ref('blue.100'),
          muted: ref('blue.200'),
          emphasized: ref('blue.300'),
          solid: ref('blue.600'),
          border: ref('blue.500'),
        },
        // Neutral (palette-less) semantic tokens Chakra's stock recipes/components
        // consume. Same reconciliation: gray-referencing members re-pointed to a single
        // step; DEFAULT/inverted (white/black-based) left to Chakra since they don't
        // double-invert. info.* references blue (mode-aware) so it is reconciled; the
        // error/warning/success neutrals reference red/orange/green (NOT mode-aware) and
        // stay stock, where Chakra's per-mode step switch is correct.
        bg: {
          subtle: ref('gray.50'),
          muted: ref('gray.100'),
          emphasized: ref('gray.200'),
          panel: { value: { _light: '{colors.white}', _dark: '{colors.gray.100}' } },
          info: ref('blue.50'),
        },
        fg: {
          DEFAULT: { value: { _light: '{colors.black}', _dark: '{colors.gray.900}' } },
          muted: ref('gray.600'),
          subtle: ref('gray.400'),
          info: ref('blue.600'),
        },
        // brand — solid stays mapped to brand.500 (now the accent)
        brand: {
          solid: { value: '{colors.brand.500}' },
          contrast: { value: 'white' },
          fg: { value: '{colors.brand.700}' },
          muted: { value: '{colors.brand.100}' },
          subtle: { value: '{colors.brand.50}' },
          emphasized: { value: '{colors.brand.200}' },
          focusRing: { value: '{colors.brand.500}' },
        },
        // chrome ladder — each surface paired with its fg
        canvas: { DEFAULT: surface(chrome.canvas), 2: surface(chrome.canvas2), fg: inkFg },
        surface: { DEFAULT: surface(chrome.surface), 2: surface(chrome.surface2), fg: inkFg },
        ink: { DEFAULT: surface(chrome.ink), 2: surface(chrome.ink2) },
        muted: { DEFAULT: surface(chrome.muted) },
        // DEFAULT/2 are our chrome border (correct dark #262C28). muted/subtle/emphasized
        // are gray-referencing stock members re-pointed single-step (emphasized fixes the
        // bright outline-button border); info follows the reconciled blue.
        border: {
          DEFAULT: surface(chrome.border),
          2: surface(chrome.border2),
          muted: ref('gray.100'),
          subtle: ref('gray.50'),
          emphasized: ref('gray.300'),
          info: ref('blue.500'),
        },
        accent: {
          DEFAULT: surface(chrome.accent),
          strong: surface(chrome.accentStrong),
          soft: surface(chrome.accentSoft),
          fg: surface(chrome.onAccent),
        },
        // fg is a genuine mode-invariant white (the band background stays dark in
        // both light and dark theme) — a named token so band-header chrome (icon
        // buttons, back links) doesn't need a raw color="white" literal per call site.
        band: { DEFAULT: surface(chrome.band), fg: surface(chrome.onBand) },
        // status — fg + tint (chip vocabulary)
        success: { fg: surface(status.success.fg), subtle: surface(status.success.tint) },
        warning: { fg: surface(status.warning.fg), subtle: surface(status.warning.tint) },
        danger: { fg: surface(status.danger.fg), subtle: surface(status.danger.tint) },
        info: { fg: surface(status.info.fg), subtle: surface(status.info.tint) },
        // A6 slice 9 — data-viz palette (off-map). Mode-aware via dualRamp; the
        // map palette is the constant JS consts in analyticsMap/viz/mapRamps.ts.
        viz: {
          cat: dualRamp(vizCat as unknown as Record<string, string>, vizCatDark as unknown as Record<string, string>),
          seq: dualRamp(vizSeq as unknown as Record<string, string>, vizSeqDark as unknown as Record<string, string>),
          div: dualRamp(vizDiv, vizDivDark),
          party: dualRamp(vizParty, vizPartyDark),
        },
      },
    },
    recipes: { ...recipes },
    // v3's stock dialog recipe shifts each size step's maxW one token notch
    // wider than v2's Modal used at the same step name; ResponsiveModal restores
    // v2 widths by pointing its public size names at other existing v3 steps.
    // Only v2's "xl" (36rem) has no equivalent, so it alone needs a new step.
    slotRecipes: {
      dialog: {
        ...defaultConfig.theme!.slotRecipes!.dialog,
        variants: {
          ...defaultConfig.theme!.slotRecipes!.dialog!.variants,
          size: {
            ...defaultConfig.theme!.slotRecipes!.dialog!.variants!.size,
            // Chakra v2 Modal size="xl" -> maxW token "xl" (36rem).
            xlCompat: { content: { maxW: 'xl' } },
          },
        },
      },
      ...slotRecipes,
    },
  },
})

export const system = createSystem(defaultConfig, config)
